import React, { memo } from "react";
import { useNavigate } from "react-router";
import { MarkdownReact, MESSAGE_PROSE_CLASS } from "@/components/MarkdownReact";
import { UserAvatar } from "@/components/UserAvatar";
import {
  OutputAsset,
  MESSAGE_SCOPE,
  isReceiveEmailTrigger,
  isReceiveSmsTrigger,
} from "@forgepoint/shared";
import type { UserMessageMetadata } from "@forgepoint/shared";
import { AssetDisplay } from "@/components/AssetDisplay";
import { MessageTimestamp } from "@/components/MessageTimestamp";
import {
  Mail,
  Phone,
  Clock,
  MessageSquare,
  Globe,
  Building2,
  ArrowRight,
} from "lucide-react";

const AGENT_THREAD_MESSAGE_TYPE = "agent_thread_message";

/** Resolves the channel tag icon for a message metadata type. */
const getChannelIcon = (type: string): React.FC<{ className?: string }> => {
  if (type === "email" || isReceiveEmailTrigger(type)) {
    return Mail;
  }
  if (type === "sms" || isReceiveSmsTrigger(type)) {
    return Phone;
  }
  if (type === "timer") {
    return Clock;
  }
  return MessageSquare;
};

interface WorkflowRunViewUserMessageProps {
  id: string;
  senderName?: string;
  senderProfilePictureUrl?: string | null;
  senderDetails?: string | null;
  content?: string;
  assets?: OutputAsset[];
  messageMetadata?: UserMessageMetadata | null;
  timestamp?: Date;
  movedFrom?: MovedMessageSource | null;
}

/** Where a message the agent moved in from another thread came from. */
export interface MovedMessageSource {
  threadId: string;
  threadName: string | null;
  movedByName: string;
}

/**
 * Renders a user message with channel/scope badges and optional attachments.
 * A message moved in from another thread shows a short notice linking back to
 * where it came from instead of its content. Memoized so unchanged messages skip re-rendering when the
 * thread list re-renders during polling.
 * @param props - The message content, sender, metadata, and move source
 * @returns The rendered user message
 */
export const WorkflowRunViewUserMessage = memo(
  function WorkflowRunViewUserMessage({
    senderName,
    senderProfilePictureUrl,
    senderDetails,
    content,
    assets,
    messageMetadata,
    timestamp,
    movedFrom,
  }: WorkflowRunViewUserMessageProps) {
    const navigate = useNavigate();
    const outputAssets =
      assets?.map((asset) => ({
        id: asset.id,
        fileName: asset.fileName,
        fileType: asset.fileType,
        fileSize: asset.fileSize,
        createdAt: asset.createdAt,
      })) ?? [];

    const ChannelIcon =
      messageMetadata && messageMetadata.type !== AGENT_THREAD_MESSAGE_TYPE
        ? getChannelIcon(messageMetadata.type)
        : null;

    return (
      <div className="flex items-start gap-3 w-full">
        <UserAvatar
          profilePictureUrl={senderProfilePictureUrl}
          name={senderName ?? "User"}
          initials={senderName?.charAt(0).toUpperCase() ?? "U"}
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm font-medium">{senderName}</div>
            {messageMetadata && ChannelIcon && (
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                <ChannelIcon className="h-3 w-3" />
                {messageMetadata.label && (
                  <span className="truncate max-w-[200px]">
                    {messageMetadata.label}
                  </span>
                )}
              </span>
            )}
            {messageMetadata?.scope === MESSAGE_SCOPE.EXTERNAL && (
              <span className="inline-flex items-center gap-1 text-xs text-orange-700 bg-orange-100 border border-orange-200 px-1.5 py-0.5 rounded">
                <Globe className="h-3 w-3" />
                External
              </span>
            )}
            {(messageMetadata?.scope === MESSAGE_SCOPE.INTERNAL ||
              messageMetadata?.scope === MESSAGE_SCOPE.USER) && (
              <span className="inline-flex items-center gap-1 text-xs text-white bg-[hsl(214_38%_34%)] border border-[hsl(214_38%_44%)] px-1.5 py-0.5 rounded">
                <Building2 className="h-3 w-3" />
                Internal
              </span>
            )}
            {timestamp && <MessageTimestamp timestamp={timestamp} />}
          </div>
          {senderDetails && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {senderDetails}
            </div>
          )}
          {movedFrom ? (
            <div className="mt-1 text-sm text-muted-foreground">
              {movedFrom.movedByName} moved a message here from{" "}
              <span className="font-medium text-foreground">
                {movedFrom.threadName || "another conversation"}
              </span>{" "}
              to avoid duplicating work.{" "}
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/standard-agent?threadId=${encodeURIComponent(movedFrom.threadId)}`,
                  )
                }
                className="group inline-flex items-center gap-0.5 rounded-sm font-medium text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                View original conversation
                <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-focus-visible:translate-x-0.5 motion-reduce:transform-none motion-reduce:transition-none" />
              </button>
            </div>
          ) : (
            <div className={`mt-1 ${MESSAGE_PROSE_CLASS}`}>
              <MarkdownReact>{content}</MarkdownReact>
            </div>
          )}
          {outputAssets.length > 0 && (
            <div className="mt-2">
              <AssetDisplay assets={outputAssets} />
            </div>
          )}
        </div>
      </div>
    );
  },
);
